import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {customerAuthApi} from '../services/api';
import {CustomerDto, CustomerLoginRequestDto, CustomerRegisterRequestDto} from '../types';
import toast from 'react-hot-toast';
import { useMerchant } from './MerchantContext';

// Define the User type based on CustomerDto
export interface User extends CustomerDto {
  name: string; // Compatibility field
}

// Define the context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (credentials: CustomerLoginRequestDto) => Promise<boolean>;
  register: (data: CustomerRegisterRequestDto) => Promise<boolean>;
  logout: () => void;
  updateUser: (data: Partial<User>) => void;
  favorites: string[];
  addFavorite: (productId: string) => void;
  removeFavorite: (productId: string) => void;
  isFavorite: (productId: string) => boolean;
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  showForgotPasswordModal: boolean;
  setShowForgotPasswordModal: (show: boolean) => void;
  showFirstAccessModal: boolean;
  setShowFirstAccessModal: (show: boolean) => void;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => false,
  register: async () => false,
  logout: () => {},
  updateUser: () => {},
  favorites: [],
  addFavorite: () => {},
  removeFavorite: () => {},
  isFavorite: () => false,
  showLoginModal: false,
  setShowLoginModal: () => {},
  showForgotPasswordModal: false,
  setShowForgotPasswordModal: () => {},
  showFirstAccessModal: false,
  setShowFirstAccessModal: () => {},
});

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showFirstAccessModal, setShowFirstAccessModal] = useState(false);
  const { setSelectedAddress } = useMerchant();

  // Check if user is already logged in and load favorites on mount
  useEffect(() => {
    // Disparado quando o refresh de token falhou (sessão realmente expirou).
    // Só chega aqui depois de já termos tentado renovar via refresh token,
    // então abrir o modal de login é o comportamento esperado.
    const handleUnauthorized = () => {
      setUser(null);
      setSelectedAddress(null);
      setShowLoginModal(true);
      toast.error('Sua sessão expirou. Por favor, faça login novamente.');
    };

    // Disparado quando o refresh renovou a sessão silenciosamente.
    // Mantém o usuário do contexto em sincronia com os novos dados/tokens.
    const handleTokenRefreshed = (event: Event) => {
      const refreshedUser = (event as CustomEvent).detail as User | undefined;
      if (refreshedUser) {
        setUser(refreshedUser);
      }
    };

    window.addEventListener('unauthorized-error', handleUnauthorized);
    window.addEventListener('token-refreshed', handleTokenRefreshed);

    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        localStorage.removeItem('user');
      }
    }

    const storedFavorites = localStorage.getItem('favorites');
    if (storedFavorites) {
      try {
        setFavorites(JSON.parse(storedFavorites));
      } catch (error) {
        console.error('Failed to parse favorites:', error);
      }
    }
    return () => {
      window.removeEventListener('unauthorized-error', handleUnauthorized);
      window.removeEventListener('token-refreshed', handleTokenRefreshed);
    };
  }, []);

  // Save favorites whenever they change
  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Login function
  const login = async (credentials: CustomerLoginRequestDto): Promise<boolean> => {
    try {
      const response = await customerAuthApi.login(credentials);
      
      const userWithFullname: User = {
        ...response.user,
        name: `${response.user.firstName} ${response.user.lastName}`.trim()
      };

      setUser(userWithFullname);
      localStorage.setItem('user', JSON.stringify(userWithFullname));
      localStorage.setItem('accessToken', response.accessToken);
      localStorage.setItem('refreshToken', response.refreshToken);
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  // Register function
  const register = async (data: CustomerRegisterRequestDto): Promise<boolean> => {
    // O registro em si (erros como "CPF já cadastrado") é propagado para quem
    // chamou, para que a UI exiba a mensagem vinda do backend.
    await customerAuthApi.register(data);

    // Auto-login após o cadastro. O backend autentica por CPF ou celular
    // (findByDocumentOrPhone) — NUNCA por e-mail. Usamos o CPF (fallback celular).
    return await login({
      cpfOrPhone: data.cpf || data.phone,
      password: data.password,
    });
  };

  // Logout function
  const logout = async () => {
    try {
      await customerAuthApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setFavorites([]);
      setSelectedAddress(null);
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('favorites');
    }
  };

  // Update user function
  const updateUser = (data: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...data };
      if (data.firstName || data.lastName) {
        updatedUser.name = `${updatedUser.firstName} ${updatedUser.lastName}`.trim();
      }
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const addFavorite = (productId: string) => {
    if (!favorites.includes(productId)) {
      setFavorites([...favorites, productId]);
    }
  };

  const removeFavorite = (productId: string) => {
    setFavorites(favorites.filter(id => id !== productId));
  };

  const isFavorite = (productId: string) => {
    return favorites.includes(productId);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        updateUser,
        favorites,
        addFavorite,
        removeFavorite,
        isFavorite,
        showLoginModal,
        setShowLoginModal,
        showForgotPasswordModal,
        setShowForgotPasswordModal,
        showFirstAccessModal,
        setShowFirstAccessModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;